import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { fade, makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import { Controller as FormController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import sendContactUs from '../../services/contactUs';
import Loader from '../loader';
import Notification from '../notification';

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup
    .string()
    .email('Email must be a valid email')
    .required('Email is required'),
  message: yup.string().required('Description is required')
});

const useStyles = makeStyles(theme => ({
  formWrap: {
    position: 'relative',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[300],
    border: `1px solid ${theme.colors.borderColor}`,
    borderRadius: 8,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8)
    }
  },
  title: {
    marginBottom: theme.spacing(8),
    fontWeight: 700
  },
  formLabel: {
    fontSize: 14
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: fade(theme.palette.common.white, 0.7)
  }
}));

const ContactForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [notification, setNotification] = React.useState({
    open: false,
    type: '',
    message: ''
  });

  const { handleSubmit, control, errors, reset } = useForm({
    validationSchema,
    defaultValues: {
      name: '',
      businessName: '',
      email: '',
      phoneNumber: '',
      message: ''
    }
  });

  const onSubmit = data => {
    setIsLoading(true);

    sendContactUs(data)
      .then(result => {
        setNotification(true);
        if (result.success) {
          return setNotification({
            open: true,
            type: 'success',
            message: 'Thank you for your inquiry!'
          });
        }
        return setNotification({
          open: true,
          type: 'error',
          message: result.message
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        reset();
        setIsLoading(false);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setNotification(false);
  };

  return (
    <>
      {notification.open && (
        <Notification
          open={notification.open}
          onClose={handleClose}
          type={notification.type}
          message={notification.message}
        />
      )}

      {/* Form */}
      <div className={classes.formWrap}>
        <Typography className={classes.title} variant="h4" align="center">
          {t('whiteLabelPage.contactUs.form.title')}
        </Typography>
        <form
          className={classes.form}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Grid container spacing={4}>
            {/* Your name */}
            <Grid item xs={12} sm={6}>
              <FormController
                as={TextField}
                control={control}
                defaultValue=""
                type="text"
                name="name"
                label={t('whiteLabelPage.contactUs.form.name')}
                fullWidth
                variant="outlined"
                disabled={isLoading}
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : ''}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helper
                  }
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <FormController
                as={TextField}
                control={control}
                defaultValue=""
                type="email"
                name="email"
                label={t('whiteLabelPage.contactUs.form.email')}
                fullWidth
                variant="outlined"
                disabled={isLoading}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helper
                  }
                }}
              />
            </Grid>

            {/* Business name  */}
            <Grid item xs={12} sm={6}>
              <FormController
                as={TextField}
                control={control}
                defaultValue=""
                type="text"
                name="businessName"
                label={t('whiteLabelPage.contactUs.form.businessName')}
                fullWidth
                variant="outlined"
                disabled={isLoading}
              />
            </Grid>

            {/* Phone number */}
            <Grid item xs={12} sm={6}>
              <FormController
                as={TextField}
                control={control}
                defaultValue=""
                type="text"
                name="phoneNumber"
                label={t('whiteLabelPage.contactUs.form.phoneNumber')}
                fullWidth
                variant="outlined"
                disabled={isLoading}
              />
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <FormController
                as={TextField}
                control={control}
                defaultValue=""
                type="text"
                name="message"
                label={t('whiteLabelPage.contactUs.form.message')}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                disabled={isLoading}
                error={!!errors.message}
                helperText={errors.message ? errors.message.message : ''}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helper
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Submit button */}
          <Box mt={4} textAlign="right">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {t('whiteLabelPage.contactUs.form.submitButton')}
            </Button>
          </Box>
        </form>

        {/* Loader */}
        {isLoading && (
          <Loader className={classes.loader} message="Sending ..." />
        )}
      </div>
    </>
  );
};

export default ContactForm;
